import { derived, writable } from 'svelte/store';
import { tradingContext } from './venueContext.js';
import { COINBASE, COINBASE_SANDBOX, isKnownVenue, toVenueId, venueById } from './venues.js';

/**
 * The run TARGET model (ADR-0040 "venue and run mode are orthogonal").
 *
 * A running strategy, a TWAP parent, a holdings read or a portfolio read is scoped by a target
 * `{ venue, mode }`: WHERE (a venue id from lib/venues.js) and HOW (the run mode's wire NAME —
 * 'Paper' | 'Live'; 'Backtest' exists server-side but is never a running strategy). Real money is
 * exactly `(Coinbase, Live)`; Paper on Coinbase is simulated fills against Coinbase's live book and
 * Live on the sandbox is fake funds.
 *
 * Everything here is fail-closed: an unrecognised venue or mode yields `null` / 'unknown', never a
 * real-money answer. The vitest grep gate (venueIdGate.test.js) keeps raw venue-id comparisons out of
 * the rest of `src/` — views ask `isRealMoney`, `targetLabel`, `matchesTarget` instead.
 *
 * contract: packages/Utilities/RunMode.cs (wire form = the name, case-insensitive on the server),
 *           packages/AutoTrader.Run/Models/DeployStrategyRequest.cs (`venue`, `mode`),
 *           packages/AutoTrader.Run/Models/LiveRunSnapshot.cs (`exchange`, `mode`),
 *           packages/Exchange.Data/Models/TwapReadModels.cs (`exchgId`, `mode`).
 */

/** The run modes a strategy can run in, in the server's wire spelling. */
export const MODE = Object.freeze({ PAPER: 'Paper', LIVE: 'Live' });

/** The first-ever / no-credential default: simulated fills on Coinbase. Never `(Coinbase, Live)`. */
export const DEFAULT_TARGET = Object.freeze({ venue: COINBASE, mode: MODE.PAPER });

/** The three targets the server will deploy (M7): Paper on Coinbase, Live on Coinbase, Live on the sandbox. */
export const DEPLOYABLE_TARGETS = Object.freeze([
  Object.freeze({
    target: DEFAULT_TARGET,
    label: 'Paper on Coinbase',
    hint: 'Simulated fills against the live Coinbase order book. No credentials, no real orders.',
    requiresSandboxCreds: false,
  }),
  Object.freeze({
    target: Object.freeze({ venue: COINBASE, mode: MODE.LIVE }),
    label: 'Live on Coinbase',
    hint: 'Real orders with real money on Coinbase Advanced — held behind the typed-name confirm.',
    requiresSandboxCreds: false,
  }),
  Object.freeze({
    target: Object.freeze({ venue: COINBASE_SANDBOX, mode: MODE.LIVE }),
    label: 'Live on Coinbase Sandbox',
    hint: "Real API, fake funds — needs the org's Coinbase Sandbox (CBX) credential in the Vault.",
    requiresSandboxCreds: true,
  }),
]);

/**
 * Normalise a wire/form mode to its canonical name, case-insensitively; anything else (a number, a
 * venue word like 'sandbox', blank) is `null`.
 */
export function normalizeMode(raw) {
  if (typeof raw !== 'string') return null;
  const lower = raw.trim().toLowerCase();
  if (lower === 'paper') return MODE.PAPER;
  if (lower === 'live') return MODE.LIVE;
  return null;
}

/**
 * Parse a loosely-typed value into a target, or return `fallback` (default `null`) when either half is
 * missing or unrecognised. Accepts the shapes the SPA meets: a stored/JSON `{ venue, mode }`, a strategy
 * row (`exchange`), a TWAP parent (`exchgId`), a deploy body (`venue`). A JSON string is parsed first.
 */
export function parseTarget(raw, fallback = null) {
  let value = raw;
  if (typeof value === 'string') {
    try {
      value = JSON.parse(value);
    } catch {
      return fallback;
    }
  }
  if (!value || typeof value !== 'object') return fallback;
  const venue = toVenueId(value.venue ?? value.exchange ?? value.exchgId);
  const mode = normalizeMode(value.mode);
  if (venue === null || !isKnownVenue(venue) || mode === null) return fallback;
  return { venue, mode };
}

/** The target a server row (strategy snapshot, TWAP parent, deploy body) carries, or `null`. */
export function targetOf(row) {
  return parseTarget(row, null);
}

/**
 * Real money iff Live on Coinbase. An unparseable target answers `false` here and 'unknown' in
 * `targetLabel` — a row the SPA cannot classify is never styled or confirmed as real money, and never
 * silently treated as paper either (the views render the UNKNOWN badge for it).
 */
export function isRealMoney(target) {
  const t = targetOf(target);
  return t !== null && t.mode === MODE.LIVE && venueById(t.venue)?.realMoney === true;
}

/**
 * The salience label a target renders under — 'paper' (simulated fills, any venue), 'sandbox' (Live on
 * fake funds), 'live' (real money) — or 'unknown' for anything unrecognised. Never 'live' by default.
 */
export function targetLabel(target) {
  const t = targetOf(target);
  if (t === null) return 'unknown';
  if (t.mode === MODE.PAPER) return 'paper';
  return venueById(t.venue)?.realMoney ? 'live' : 'sandbox';
}

/** `targetLabel` in badge form: 'PAPER' | 'SANDBOX' | 'LIVE' | 'UNKNOWN'. */
export function targetBadge(target) {
  return targetLabel(target).toUpperCase();
}

/** A one-line description for configuration panels: "Paper on Coinbase Advanced"; 'Unknown target' when unparseable. */
export function describeTarget(target) {
  const t = targetOf(target);
  if (t === null) return 'Unknown target';
  return `${t.mode} on ${venueById(t.venue).name}`;
}

/** Structural equality of two targets (either may be loosely typed or null). */
export function sameTarget(a, b) {
  const ta = targetOf(a);
  const tb = targetOf(b);
  if (ta === null || tb === null) return ta === tb;
  return ta.venue === tb.venue && ta.mode === tb.mode;
}

/** A stable string key for stale guards / reconnect keys: "2:Paper"; '' for an unparseable target. */
export function targetKey(target) {
  const t = targetOf(target);
  return t === null ? '' : `${t.venue}:${t.mode}`;
}

/** Whether a server row belongs to a view's target — the dashboard list filter. Unparseable rows never match. */
export function matchesTarget(row, target) {
  const t = targetOf(target);
  return t !== null && sameTarget(row, t);
}

/** The fields a deploy body carries for its target: `{ venue, mode }` — never the retired `Exchange`. */
export function deployTargetFields(target) {
  const t = targetOf(target);
  if (t === null) throw new Error('A deploy needs a recognised run target (venue + mode).');
  return { venue: t.venue, mode: t.mode };
}

/**
 * Map today's header contexts onto run targets. The Live context is `(Coinbase, Live)`; its Paper
 * Trading view (`view === 'paper'`) is `(Coinbase, Paper)`; the Sandbox context is `(Sandbox, Live)`.
 * The Paper view does not exist inside the Sandbox context (ADR-0012 AC 3), so `view` is ignored there.
 */
export function targetFor(context, view = null) {
  if (context === 'sandbox') return { venue: COINBASE_SANDBOX, mode: MODE.LIVE };
  return { venue: COINBASE, mode: view === 'paper' ? MODE.PAPER : MODE.LIVE };
}

/** The header context's own target — what the manual trading surfaces (holdings, orders) read under. */
export const contextTarget = derived(tradingContext, (ctx) => targetFor(ctx));

/**
 * The optional run-mode filter the SSE stream is narrowed to (ADR-0040 M12: `?mode=Paper`). `null` =
 * unscoped. A mode-specific view (the Paper Trading dashboard) sets it on mount and clears it on
 * destroy; sseStore re-scopes the connection when it changes. Mode-agnostic events still flow.
 */
export const streamModeFilter = writable(null);

/** Set (or clear with `null`) the SSE mode filter; junk values clear it rather than scope the stream to nothing. */
export function setStreamModeFilter(mode) {
  streamModeFilter.set(normalizeMode(mode));
}
